/**
 * 统计卡片组件
 */

import React from 'react';
import { Card, Statistic, Row, Col, Space, Typography } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';

const { Text } = Typography;

export interface StatisticCardProps {
  title: string;
  value: number | string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  precision?: number;
  
  // 趋势相关
  trend?: {
    value: number | string;
    label?: string;
    isUp?: boolean;
  };
  
  // 额外信息
  extra?: React.ReactNode;
  footer?: React.ReactNode;
  
  // 样式相关
  loading?: boolean;
  size?: 'small' | 'default' | 'large';
  bordered?: boolean;
  hoverable?: boolean;
  
  // 事件
  onClick?: () => void;
  
  // Card属性
  cardProps?: any;
}

/**
 * 统计卡片组件
 */
const StatisticCard: React.FC<StatisticCardProps> = ({
  title,
  value,
  prefix,
  suffix,
  precision,
  trend,
  extra,
  footer,
  loading = false,
  size = 'default',
  bordered = true,
  hoverable = false,
  onClick,
  cardProps,
}) => {
  // 渲染趋势指示器
  const renderTrend = () => {
    if (!trend) return null;

    const { value: trendValue, label, isUp = true } = trend;
    const color = isUp ? '#3f8600' : '#cf1322';
    const icon = isUp ? <ArrowUpOutlined /> : <ArrowDownOutlined />;

    return (
      <Space size="small">
        <Text style={{ color, fontSize: 12 }}>
          {icon} {trendValue}
        </Text>
        {label && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {label}
          </Text>
        )}
      </Space>
    );
  };

  // 计算卡片样式
  const getCardStyle = () => {
    const baseStyle: React.CSSProperties = {};
    
    if (onClick) {
      baseStyle.cursor = 'pointer';
    }
    
    return baseStyle;
  };

  // 计算统计组件大小
  const getStatisticSize = () => {
    switch (size) {
      case 'small':
        return { valueStyle: { fontSize: 20 } };
      case 'large':
        return { valueStyle: { fontSize: 32 } };
      default:
        return { valueStyle: { fontSize: 24 } };
    }
  };

  return (
    <Card
      loading={loading}
      bordered={bordered}
      hoverable={hoverable}
      size={size}
      style={getCardStyle()}
      onClick={onClick}
      {...cardProps}
    >
      <Row>
        <Col span={24}>
          {/* 标题和额外信息 */}
          <Row justify="space-between" align="top" style={{ marginBottom: 8 }}>
            <Col>
              <Text type="secondary" style={{ fontSize: 14 }}>
                {title}
              </Text>
            </Col>
            {extra && (
              <Col>
                {extra}
              </Col>
            )}
          </Row>

          {/* 数值统计 */}
          <Row style={{ marginBottom: trend ? 8 : 0 }}>
            <Col span={24}>
              <Statistic
                value={value}
                prefix={prefix}
                suffix={suffix}
                precision={precision}
                {...getStatisticSize()}
              />
            </Col>
          </Row>

          {/* 趋势信息 */}
          {trend && (
            <Row>
              <Col span={24}>
                {renderTrend()}
              </Col>
            </Row>
          )}

          {/* 底部信息 */}
          {footer && (
            <Row style={{ marginTop: 16 }}>
              <Col span={24}>
                {footer}
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </Card>
  );
};

export default StatisticCard;